export const ACCENT = "#F59E0B";
export const C = { mint: "#10B981", teal: "#2DD4BF", amber: "#F59E0B", coral: "#F43F5E", blue: "#3B82F6", purple: "#8B5CF6", grey: "#7a8fb5" };
export const GROUPS = [["lob", "LOB"], ["skill", "Skill"], ["tl", "TL"], ["form", "Quality Form"]];
export const SITES = ["All", "Qatar", "India"];
let BASIS = "interaction";
export const setBasis = (b) => { BASIS = b; };
export const dk = (e) => (BASIS === "interaction" && e.sd ? e.sd : e.d);
export const isLate = (e) => !!(e.sd && e.d.slice(0, 7) > e.sd.slice(0, 7));
export const lagDays = (e) => (e.sd ? Math.round((Date.parse(e.d + "T00:00:00Z") - Date.parse(e.sd + "T00:00:00Z")) / 86400000) : null);

export const mean = (a) => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : null);
export const fmtN = (n, d = 1) => (n == null || isNaN(n) ? "—" : Number(n).toLocaleString(undefined, { maximumFractionDigits: d }));
export const fmtPct = (n, d = 1) => (n == null || isNaN(n) ? "—" : `${Number(n).toFixed(d)}%`);
export const fmtInt = (n) => (n == null || isNaN(n) ? "—" : Math.round(n).toLocaleString());
export const covLabel = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("en", { month: "short", day: "numeric" }) : "—");

export const bandOf = (score, t) => (score == null ? "none" : score >= t.excellent ? "excellent" : score >= t.pmt ? "pass" : score >= t.coaching ? "below" : "coaching");
export const BAND_COLOR = { excellent: C.mint, pass: C.teal, below: C.amber, coaching: C.coral, none: C.grey };
export const BAND_LABEL = { excellent: "Excellent", pass: "On target", below: "Below target", coaching: "Needs coaching", none: "—" };
export const bandTone = (b) => ({ excellent: "good", pass: "good", below: "warn", coaching: "bad", none: "neutral" }[b]);

const addDays = (iso, n) => { const d = new Date(iso + "T00:00:00Z"); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
const monthEnd = (ym) => { const [y, m] = ym.split("-").map(Number); return `${ym}-${String(new Date(Date.UTC(y, m, 0)).getUTCDate()).padStart(2, "0")}`; };
export const monthLabel = (m) => new Date(Number(m.slice(0, 4)), Number(m.slice(5, 7)) - 1, 1).toLocaleString("en", { month: "short", year: "numeric" });

export function presetsFor(data) {
  const dates = data.evals.map(dk).sort();
  const latest = dates[dates.length - 1];
  const first = dates[0];
  const month = latest.slice(0, 7);
  const months = [...new Set(dates.map((d) => d.slice(0, 7)))].sort().reverse().slice(0, 6);
  const list = [
    ...months.map((m, i) => ({ id: i === 0 ? "month" : i === 1 ? "prev" : `m-${m}`, label: monthLabel(m), from: `${m}-01`, to: m === month ? latest : monthEnd(m) })),
    { id: "30d", label: "Last 30 days", from: addDays(latest, -29), to: latest },
    { id: "all", label: "All evaluations", from: first, to: latest },
  ];
  return { month, latest, first, months, list };
}

export function prevRange(from, to) {
  const len = Math.round((Date.parse(to + "T00:00:00Z") - Date.parse(from + "T00:00:00Z")) / 86400000) + 1;
  return { from: addDays(from, -len), to: addDays(from, -1) };
}

export function filterData(data, { from, to, group, value, site = "All" }) {
  const inR = (d) => d >= from && d <= to;
  const okG = (e) => (value === "All" || e[group] === value) && (site === "All" || e.loc === site);
  const evals = data.evals.filter((e) => inR(dk(e)) && okG(e));
  const ciscos = value === "All" ? null : new Set(evals.map((e) => e.c));
  const qsl = data.qsl.filter((q) => inR(q.d) && (!ciscos || ciscos.has(q.c)) && (site === "All" || q.loc === site));
  return { evals, qsl };
}

export function statsOf(evals, t, qsl = []) {
  const scored = evals.filter((e) => e.score != null);
  const sc = scored.map((e) => e.score);
  const n = scored.length;
  const qs = qsl.map((q) => q.score);
  return {
    evals: evals.length, avg: mean(sc),
    pass: n ? (100 * sc.filter((s) => s >= t.pmt).length) / n : null,
    excellent: n ? (100 * sc.filter((s) => s >= t.excellent).length) / n : null,
    coaching: n ? (100 * sc.filter((s) => s < t.coaching).length) / n : null,
    fatal: evals.filter((e) => e.fatal).length,
    fatalRate: evals.length ? (100 * evals.filter((e) => e.fatal).length) / evals.length : null,
    agents: new Set(evals.map((e) => e.c)).size,
    withSd: evals.filter((e) => e.sd).length,
    late: evals.some((e) => e.sd) ? (100 * evals.filter(isLate).length) / evals.filter((e) => e.sd).length : null,
    lateN: evals.filter(isLate).length,
    qsl: qs.length ? mean(qs) : null, audits: qs.length,
  };
}

export function dailySeries(evals, t) {
  const by = {};
  evals.forEach((e) => { (by[dk(e)] = by[dk(e)] || []).push(e); });
  return Object.keys(by).sort().map((d) => {
    const s = statsOf(by[d], t);
    return { d, label: d.slice(5), n: s.evals, avg: s.avg, pass: s.pass, fatal: s.fatal, fatalRate: s.fatalRate, agents: s.agents, late: s.late, excellent: s.excellent, coaching: s.coaching };
  });
}

export function weeklySeries(evals, t) {
  const by = {};
  evals.forEach((e) => {
    const dt = new Date(dk(e) + "T00:00:00Z");
    const monday = new Date(dt); monday.setUTCDate(dt.getUTCDate() - ((dt.getUTCDay() + 6) % 7));
    const k = monday.toISOString().slice(0, 10);
    (by[k] = by[k] || []).push(e);
  });
  return Object.keys(by).sort().map((d) => ({ d, label: `wk ${d.slice(5)}`, ...statsOf(by[d], t), n: by[d].length }));
}

export function groupBy(evals, key, t, limit = 0) {
  const by = {};
  evals.forEach((e) => { (by[e[key] || "Unknown"] = by[e[key] || "Unknown"] || []).push(e); });
  let rows = Object.entries(by).map(([k, rs]) => ({ key: k, ...statsOf(rs, t), rows: rs })).sort((a, b) => b.evals - a.evals);
  if (limit && rows.length > limit) {
    const rest = rows.slice(limit).flatMap((r) => r.rows);
    rows = [...rows.slice(0, limit), { key: "Other", ...statsOf(rest, t), rows: rest }];
  }
  return rows;
}

export function histogram(evals, t) {
  const b = Array.from({ length: 10 }, (_, i) => ({ lo: i * 10, label: `${i * 10}–${i * 10 + 9}`, n: 0, band: bandOf(i * 10 + 5, t) }));
  b.push({ lo: 100, label: "100", n: 0, band: "excellent" });
  evals.forEach((e) => { if (e.score != null) b[Math.min(10, Math.floor(e.score / 10))].n += 1; });
  return b;
}

export function paramStats(evals, params) {
  return params.map((p) => {
    const vals = evals.map((e) => e.p[p.n - 1]).filter((v) => v != null);
    const passes = vals.filter((v) => v >= p.max).length;
    return { ...p, scored: vals.length, pass: vals.length ? (100 * passes) / vals.length : null, avg: mean(vals), fails: vals.length - passes };
  });
}

export function evaluatorStats(evals, t) {
  const lobAvg = {};
  groupBy(evals, "lob", t).forEach((g) => { lobAvg[g.key] = g.avg; });
  return groupBy(evals, "ev", t).map((g) => {
    const mix = {};
    g.rows.forEach((r) => { mix[r.lob] = (mix[r.lob] || 0) + 1; });
    const expected = g.rows.length ? Object.entries(mix).reduce((a, [l, n]) => a + (lobAvg[l] ?? 0) * n, 0) / g.rows.length : null;
    const strict = g.avg != null && expected != null ? g.avg - expected : null;
    return { ...g, evid: g.rows[0]?.evid, lobs: Object.keys(mix).length, mix, strict, flag: strict == null ? "aligned" : strict > 5 ? "lenient" : strict < -5 ? "strict" : "aligned" };
  });
}

export function agentStats(evals, qsl, t) {
  const qBy = {};
  qsl.forEach((q) => { (qBy[q.c] = qBy[q.c] || []).push(q.score); });
  return groupBy(evals, "c", t).map((g) => {
    const last = g.rows.reduce((a, r) => (dk(r) > dk(a) ? r : a), g.rows[0]);
    return { ...g, c: g.key, agent: last.agent, tl: last.tl, lob: last.lob, loc: last.loc, last: dk(last), qsl: qBy[g.key]?.length ? mean(qBy[g.key]) : null };
  });
}

/* ── audit model (document-driven rebuild) ───────────────────────────────── */
export const RATINGS = [["S", "Standard", C.mint], ["NC", "Non-Critical", C.amber], ["C", "Critical", C.coral]];
export const RC = { S: C.mint, NC: C.amber, C: C.coral };
export const teamOf = (e) => `${e.tl} — ${e.lob}`;
export const ym = (e) => dk(e).slice(0, 7);
export const monthsOf = (evals) => [...new Set(evals.map(ym))].sort();
export const isoWeek = (iso) => {
  const d = new Date(iso + "T00:00:00Z"); const day = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - day + 3);
  const y1 = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  return `${d.getUTCFullYear()}-W${String(1 + Math.round(((d - y1) / 86400000 - 3 + ((y1.getUTCDay() + 6) % 7)) / 7)).padStart(2, "0")}`;
};
export const pct = (a, b) => (b ? (100 * a) / b : null);
export const scoreColor = (v, t) => (v == null ? C.grey : v >= t.pmt ? C.mint : v >= t.coaching ? C.amber : C.coral);
export const compColor = (v, t) => (v == null ? C.grey : v <= t.fatal_threshold / 2 ? C.mint : v <= t.fatal_threshold ? C.amber : C.coral);
export const arrow = (d, lowerBetter = false) => (d == null || Math.abs(d) < 0.05 ? { sym: "•", tone: "neutral", color: C.grey } : (d > 0) !== lowerBetter ? { sym: "▲", tone: "good", color: C.mint } : { sym: "▼", tone: "bad", color: C.coral });

export function auditStats(evals, t = {}) {
  const sc = evals.map((e) => e.score).filter((v) => v != null);
  const mix = { S: 0, NC: 0, C: 0, none: 0 };
  evals.forEach((e) => { mix[e.rt || "none"] += 1; });
  const rated = mix.S + mix.NC + mix.C;
  const at100 = sc.filter((v) => v >= 100).length;
  return {
    n: evals.length, avg: mean(sc), min: sc.length ? Math.min(...sc) : null, mix, rated,
    pS: pct(mix.S, rated), pNC: pct(mix.NC, rated), pC: pct(mix.C, rated),
    crit: mix.C, critPct: pct(mix.C, evals.length), at100, at100Pct: pct(at100, sc.length),
    agents: new Set(evals.map((e) => e.c)).size, evaluators: new Set(evals.map((e) => e.ev)).size,
  };
}

export function byKey(evals, fn) {
  const by = {};
  evals.forEach((e) => { const k = fn(e); (by[k] = by[k] || []).push(e); });
  return by;
}

export function paramPass(evals, params) {
  return params.map((p) => {
    const vals = evals.map((e) => e.p[p.n - 1]).filter((v) => v != null);
    const fails = vals.filter((v) => v < p.max).length;
    return { ...p, scored: vals.length, fails, pass: pct(vals.length - fails, vals.length), failRate: pct(fails, vals.length) };
  });
}

export function defaulters(evals, params, min = 3) {
  return params.map((p) => {
    const by = {};
    evals.forEach((e) => { const v = e.p[p.n - 1]; if (v != null && v < p.max) { const x = (by[e.c] = by[e.c] || { c: e.c, agent: e.agent, tl: e.tl, lob: e.lob, misses: 0 }); x.misses += 1; } });
    return { n: p.n, label: p.label, list: Object.values(by).filter((x) => x.misses >= min).sort((a, b) => b.misses - a.misses) };
  });
}

export function agentRows(evals, t) {
  return Object.values(byKey(evals, (e) => e.c)).map((rs) => {
    const last = rs.reduce((a, r) => (dk(r) > dk(a) ? r : a), rs[0]);
    return { c: last.c, agent: last.agent || last.c, lob: last.lob, tl: last.tl, loc: last.loc, team: teamOf(last), ...auditStats(rs, t), rows: rs };
  });
}

export function teamRows(evals, params, t) {
  return Object.values(byKey(evals, teamOf)).map((rs) => {
    const pp = paramPass(rs, params).filter((p) => p.scored).sort((a, b) => a.pass - b.pass);
    return { team: teamOf(rs[0]), tl: rs[0].tl, lob: rs[0].lob, size: new Set(rs.map((e) => e.c)).size, weakest: pp[0] || null, ...auditStats(rs, t) };
  });
}

export function monthSeries(evals, t) {
  return Object.entries(byKey(evals, ym)).sort(([a], [b]) => a.localeCompare(b)).map(([m, rs]) => ({ m, label: monthLabel(m), ...auditStats(rs, t) }));
}
